import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/authFetch';
import { EmailRenderer } from './EmailRenderer';

interface Thread {
    id: string;
    direction: 'inbound' | 'outbound';
    fromEmail: string;
    toEmail: string;
    subject: string;
    bodyText: string;
    bodyHtml?: string;
    receivedAt: string;
    isRead: boolean;
    leadId?: string;
}

type FilterStatus = 'all' | 'inbound' | 'outbound' | 'bounced';

export function InboxPage() {
    const [threads, setThreads] = useState<Thread[]>([]);
    const [filter, setFilter] = useState<FilterStatus>('all');
    const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
    const [selectedLeadThreads, setSelectedLeadThreads] = useState<Thread[]>([]);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadInbox = useCallback(async () => {
        try {
            const res = await authFetch('/emails/all');
            const data = await res.json();
            setThreads(data.emails || []);
        } catch { }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadInbox();
        const t = setInterval(loadInbox, 30000);
        return () => clearInterval(t);
    }, [loadInbox]);

    const loadLeadThreads = async (leadId: string) => {
        try {
            const res = await authFetch(`/leads/${leadId}/threads`);
            const data = await res.json();
            setSelectedLeadThreads(data.threads || []);
        } catch { }
    };

    const isBounce = (t: Thread) => t.direction === 'inbound' && (
        t.fromEmail.toLowerCase().includes('mailer-daemon') ||
        t.subject.toLowerCase().includes('undelivered') ||
        t.subject.toLowerCase().includes('delivery status') ||
        t.subject.toLowerCase().includes('returned to sender')
    );

    const filtered = threads.filter(t => {
        if (filter === 'inbound') return t.direction === 'inbound' && !isBounce(t);
        if (filter === 'outbound') return t.direction === 'outbound';
        if (filter === 'bounced') return isBounce(t);
        return true;
    });

    const inboundCount = threads.filter(t => t.direction === 'inbound' && !isBounce(t)).length;
    const outboundCount = threads.filter(t => t.direction === 'outbound').length;
    const bounceCount = threads.filter(isBounce).length;
    const unreadCount = threads.filter(t => t.direction === 'inbound' && !t.isRead && !isBounce(t)).length;

    const sendReply = async () => {
        if (!replyText.trim() || !selectedThread) return;
        setSending(true);
        try {
            await authFetch('/emails/reply', {
                method: 'POST',
                body: JSON.stringify({
                    leadId: selectedThread.leadId,
                    toEmail: selectedThread.fromEmail,
                    fromEmail: selectedThread.toEmail,
                    subject: `Re: ${selectedThread.subject}`,
                    bodyText: replyText,
                    inReplyToMessageId: selectedThread.id,
                }),
            });
            setReplyText('');
            if (selectedThread.leadId) loadLeadThreads(selectedThread.leadId);
            loadInbox();
        } catch (e) { alert(String(e)); }
        finally { setSending(false); }
    };

    const markRead = async (thread: Thread) => {
        if (!thread.isRead && thread.direction === 'inbound') {
            try { await authFetch(`/emails/${thread.id}/read`, { method: 'PATCH' }); } catch { }
        }
    };

    const stripHtml = (s: string) => {
        if (!s) return '';
        // Use DOMParser for proper HTML text extraction
        try {
            const doc = new DOMParser().parseFromString(s, 'text/html');
            // Remove style and script elements
            doc.querySelectorAll('style, script, head').forEach(el => el.remove());
            return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
        } catch {
            return s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
        }
    };

    const formatTime = (dateStr: string) => {
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffH = diffMs / 3600000;
        if (diffH < 1) return `${Math.floor(diffMs / 60000)}m ago`;
        if (diffH < 24) return `${Math.floor(diffH)}h ago`;
        if (diffH < 48) return 'Yesterday';
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <div style={{ height: 'calc(100vh - var(--header-height) - 48px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <h2 className="page-title">📬 Mail</h2>
                    <p className="page-subtitle">
                        {threads.length} emails · {unreadCount > 0 && <span style={{ color: '#a855f7', fontWeight: 700 }}>{unreadCount} unread</span>}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    {([
                        { key: 'all' as FilterStatus, label: `All (${threads.length})` },
                        { key: 'outbound' as FilterStatus, label: `📤 Sent (${outboundCount})` },
                        { key: 'inbound' as FilterStatus, label: `📥 Replies (${inboundCount})` },
                        ...(bounceCount > 0 ? [{ key: 'bounced' as FilterStatus, label: `❌ Bounced (${bounceCount})` }] : []),
                    ]).map(f => (
                        <button key={f.key} className={`filter-chip${filter === f.key ? ' active' : ''}`}
                            onClick={() => setFilter(f.key)}>
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{
                flex: 1, display: 'grid', gridTemplateColumns: '380px 1fr',
                borderRadius: 'var(--radius-lg)', overflow: 'hidden',
                border: '1px solid var(--border-default)',
            }}>
                {/* ── Left: email list ── */}
                <div className="split-left" style={{
                    background: 'var(--bg-surface)', overflowY: 'auto',
                    borderRight: '1px solid var(--border-default)',
                }}>
                    {loading ? (
                        <div className="empty-state">
                            <div className="empty-icon" style={{ animation: 'pulse 1.5s infinite' }}>⏳</div>
                            <p>Loading emails...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">📭</div>
                            <p>No emails found</p>
                            <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                                {filter !== 'all' ? 'Try switching the filter' : 'Send your first email to get started'}
                            </p>
                        </div>
                    ) : (
                        filtered.map((t) => (
                            <div
                                key={t.id}
                                className={`mail-item${selectedThread?.id === t.id ? ' active' : ''}${!t.isRead && t.direction === 'inbound' ? ' unread' : ''}`}
                                onClick={() => {
                                    setSelectedThread(t);
                                    setSelectedLeadThreads([]);
                                    markRead(t);
                                    if (t.leadId) loadLeadThreads(t.leadId);
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                {/* Row 1: direction icon + sender/recipient + time */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{
                                            fontSize: 10,
                                            padding: '1px 5px',
                                            borderRadius: 4,
                                            fontWeight: 700,
                                            background: t.direction === 'inbound'
                                                ? 'rgba(34,197,94,0.15)' : 'rgba(168,85,247,0.15)',
                                            color: t.direction === 'inbound'
                                                ? '#22c55e' : '#a855f7',
                                        }}>
                                            {t.direction === 'inbound' ? '↓ IN' : '↑ OUT'}
                                        </span>
                                        <span className="mail-from" style={{ fontWeight: !t.isRead && t.direction === 'inbound' ? 700 : 500 }}>
                                            {t.direction === 'inbound' ? t.fromEmail : t.toEmail}
                                        </span>
                                    </div>
                                    <span className="mail-time">{formatTime(t.receivedAt)}</span>
                                </div>

                                {/* Row 2: subject */}
                                <span className="mail-subject" style={{
                                    fontWeight: !t.isRead && t.direction === 'inbound' ? 700 : 400,
                                }}>
                                    {t.subject || '(No subject)'}
                                </span>

                                {/* Row 3: preview */}
                                <span className="mail-preview">
                                    {stripHtml(t.bodyText).slice(0, 100) || '(Empty)'}
                                </span>
                            </div>
                        ))
                    )}
                </div>

                {/* ── Right: email detail ── */}
                <div className="split-right" style={{
                    background: 'var(--bg-base)', display: 'flex', flexDirection: 'column',
                }}>
                    {!selectedThread ? (
                        <div className="empty-state" style={{ flex: 1 }}>
                            <div className="empty-icon">💬</div>
                            <p>Select an email to read</p>
                            <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                                Click on any email from the list
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Email header */}
                            <div style={{
                                padding: '16px 20px', borderBottom: '1px solid var(--border-default)',
                                background: 'var(--bg-surface)',
                            }}>
                                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px 0', color: 'var(--text-primary)' }}>
                                    {selectedThread.subject || '(No subject)'}
                                </h3>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ fontSize: 13 }}>
                                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                            {selectedThread.direction === 'inbound' ? selectedThread.fromEmail : selectedThread.toEmail}
                                        </span>
                                        <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>
                                            → {selectedThread.direction === 'inbound' ? selectedThread.toEmail : selectedThread.fromEmail}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{
                                            fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700,
                                            background: selectedThread.direction === 'inbound'
                                                ? 'rgba(34,197,94,0.15)' : 'rgba(168,85,247,0.15)',
                                            color: selectedThread.direction === 'inbound'
                                                ? '#22c55e' : '#a855f7',
                                        }}>
                                            {selectedThread.direction === 'inbound' ? '📥 Received' : '📤 Sent'}
                                        </span>
                                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                            {new Date(selectedThread.receivedAt).toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Email body with thread */}
                            <div style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
                                {/* If we have a lead thread, show conversation */}
                                {selectedLeadThreads.length > 1 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {selectedLeadThreads.map((t) => (
                                            <div key={t.id} style={{
                                                background: t.direction === 'outbound' ? 'rgba(168,85,247,0.06)' : 'var(--bg-elevated)',
                                                border: `1px solid ${t.direction === 'outbound' ? 'rgba(168,85,247,0.15)' : 'var(--border-default)'}`,
                                                borderRadius: 'var(--radius-lg)',
                                                overflow: 'hidden',
                                            }}>
                                                {/* Mini header */}
                                                <div style={{
                                                    padding: '8px 14px',
                                                    borderBottom: '1px solid var(--border-subtle)',
                                                    fontSize: 11,
                                                    color: 'var(--text-muted)',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}>
                                                    <span>
                                                        {t.direction === 'outbound'
                                                            ? `📤 You → ${t.toEmail}`
                                                            : `📥 ${t.fromEmail} → ${t.toEmail}`}
                                                    </span>
                                                    <span>{new Date(t.receivedAt).toLocaleString()}</span>
                                                </div>
                                                {/* Content */}
                                                <div style={{ padding: '0 4px' }}>
                                                    <EmailRenderer bodyHtml={t.bodyHtml} bodyText={t.bodyText} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* Single email view */
                                    <div style={{
                                        background: 'var(--bg-elevated)',
                                        border: '1px solid var(--border-default)',
                                        borderRadius: 'var(--radius-lg)',
                                        overflow: 'hidden',
                                    }}>
                                        <EmailRenderer bodyHtml={selectedThread.bodyHtml} bodyText={selectedThread.bodyText} />
                                    </div>
                                )}
                            </div>

                            {/* Reply box (only for inbound emails) */}
                            {selectedThread.direction === 'inbound' && (
                                <div style={{
                                    padding: 16, borderTop: '1px solid var(--border-default)',
                                    display: 'flex', gap: 8, background: 'var(--bg-surface)',
                                }}>
                                    <textarea
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder="Type your reply..."
                                        className="input"
                                        rows={3}
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        className="btn btn-success"
                                        onClick={sendReply}
                                        disabled={sending || !replyText.trim()}
                                        style={{ alignSelf: 'flex-end' }}
                                    >
                                        {sending ? '...' : '📤 Send'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
